import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAvatarPlaceholder } from '../../utils/avatarPlaceholder';
import './ParentSidebar.css';

const ParentSidebar = ({ activeSection, setActiveSection, isOpen = false, onClose }) => {
  const { t } = useTranslation('parent');
  const [profileImage, setProfileImage] = useState(getAvatarPlaceholder('P', 150));
  
  // Get user info from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  const capitalizeName = (name) => {
    if (!name) return '';
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  };
  
  const getUserDisplayName = () => {
    if (user.first_name && user.last_name) {
      return `${capitalizeName(user.first_name)} ${capitalizeName(user.last_name)}`.trim();
    }
    if (user.first_name) {
      return capitalizeName(user.first_name);
    }
    if (user.name) {
      const nameParts = user.name.split(' ');
      if (nameParts.length >= 2) {
        return `${capitalizeName(nameParts[0])} ${capitalizeName(nameParts.slice(1).join(' '))}`.trim();
      }
      return capitalizeName(nameParts[0]);
    }
    if (user.email) {
      return capitalizeName(user.email.split('@')[0]);
    }
    return t('sidebar.parent');
  };
  
  const getUserInitials = () => {
    if (user.first_name && user.last_name) {
      return (user.first_name[0] + user.last_name[0]).toUpperCase();
    }
    if (user.first_name) {
      return user.first_name.substring(0, 2).toUpperCase();
    }
    if (user.name) {
      const parts = user.name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return user.name.substring(0, 2).toUpperCase();
    }
    if (user.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return 'PA';
  };
  
  const displayName = getUserDisplayName();
  const userInitials = getUserInitials();

  const navSections = [
    {
      title: t('sidebar.overview'),
      items: [
        { id: 'dashboard', label: t('sidebar.dashboard'), icon: 'dashboard', badge: null },
        { id: 'children', label: t('sidebar.children'), icon: 'people', badge: null },
        { id: 'progress', label: t('sidebar.progress'), icon: 'trending_up', badge: null }
      ]
    },
    {
      title: t('sidebar.learning'),
      items: [
        { id: 'explore-courses', label: t('sidebar.exploreCourses'), icon: 'explore', badge: null },
        { id: 'my-courses', label: t('sidebar.myCourses'), icon: 'school', badge: null },
        { id: 'vocabulary', label: t('sidebar.vocabulary'), icon: 'translate', badge: null },
        { id: 'practice', label: t('sidebar.practice'), icon: 'fitness_center', badge: null },
        { id: 'course-library', label: t('sidebar.courseLibrary'), icon: 'library_books', badge: null }
      ]
    },
    {
      title: t('sidebar.communication'),
      items: [
        { id: 'talk', label: t('sidebar.freeTalk'), icon: 'chat', badge: null },
        { id: 'virtual-classroom', label: t('sidebar.virtualClassroom'), icon: 'video_camera_front', badge: null },
        { id: 'live-learning', label: t('sidebar.liveSessions'), icon: 'live_tv', badge: null }
      ]
    },
    {
      title: t('sidebar.management'),
      items: [
        { id: 'payments', label: t('sidebar.payments'), icon: 'payment', badge: null },
        { id: 'courses', label: t('sidebar.courses'), icon: 'school', badge: null },
        { id: 'controls', label: t('sidebar.parentalControls'), icon: 'security', badge: null }
      ]
    },
    {
      title: t('sidebar.tools'),
      items: [
        { id: 'assignments', label: t('sidebar.assignments'), icon: 'assignment', badge: null }
      ]
    },
    {
      title: t('sidebar.resources'),
      items: [
        { id: 'shop', label: t('sidebar.shop'), icon: 'shopping_cart', badge: null }
      ]
    },
    {
      title: t('sidebar.account'),
      items: [
        { id: 'notifications', label: t('sidebar.notifications'), icon: 'notifications', badge: null },
        { id: 'support', label: t('sidebar.support'), icon: 'help', badge: null },
        { id: 'profile-settings', label: t('sidebar.profileSettings'), icon: 'person', badge: null },
        { id: 'account-settings', label: t('sidebar.accountSettings'), icon: 'settings', badge: null }
      ]
    }
  ];

  return (
    <div className={`parent-sidebar ${isOpen ? 'open' : ''}`}>
      {onClose && (
        <button
          type="button"
          className="parent-sidebar-close-btn"
          onClick={onClose}
          aria-label={t('sidebar.closeMenu')}
        >
          <i className="material-icons">close</i>
        </button>
      )}
      <div className="parent-sidebar-header">
        <div className="parent-profile-section">
          <div className="parent-avatar">
            {profileImage ? (
              <img src={profileImage} alt={displayName} />
            ) : (
              <div className="parent-avatar-placeholder">
                {userInitials}
              </div>
            )}
          </div>
          <div className="parent-info">
            <h3 className="parent-name">{displayName}</h3>
            <p className="parent-role">{t('sidebar.parent')}</p>
          </div>
        </div>
      </div>

      <nav className="parent-nav">
        {navSections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="parent-nav-section">
            <h4 className="parent-nav-section-title">{section.title}</h4>
            <ul className="parent-nav-list">
              {section.items.map((item) => (
                <li key={item.id}>
                  <button
                    className={`parent-nav-link ${activeSection === item.id ? 'active' : ''}`}
                    onClick={() => setActiveSection(item.id)}
                  >
                    <i className="material-icons">{item.icon}</i>
                    <span className="parent-nav-text">{item.label}</span>
                    {item.badge && (
                      <span className="parent-nav-badge">{item.badge}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="parent-sidebar-footer">
        <button 
          className="parent-settings-btn"
          onClick={() => setActiveSection('account-settings')}
        >
          <i className="material-icons">settings</i>
          <span>{t('sidebar.settings')}</span>
        </button>
      </div>
    </div>
  );
};

export default ParentSidebar;

